import { DebugLogger } from '@affine/debug';

export const CANVAS_RECONCILIATION_DIAGNOSTIC_EVENT =
  'streamlain:canvas-reconciliation-diagnostic';

export type ReconciliationPhase = 'observing' | 'mutating' | 'readonly';

export type ReconciliationAnomalyCode =
  | 'create-delete-churn'
  | 'delete-create-churn'
  | 'duplicate-existing-key'
  | 'duplicate-required-key'
  | 'missing-required-key'
  | 'mutation-burst'
  | 'mutation-without-user-intent';

export interface ReconciliationObservation {
  readonly reconciler: string;
  readonly docId: string;
  readonly phase: ReconciliationPhase;
  readonly pass: number;
  readonly existingByKey: ReadonlyMap<string, readonly string[]>;
  readonly requiredKeys: readonly string[];
  readonly created: readonly string[];
  readonly deleted: readonly string[];
  readonly updated: readonly string[];
  readonly protectedKeys: readonly string[];
}

export interface ReconciliationDiagnosticDetail {
  readonly reconciler: string;
  readonly docId: string;
  readonly phase: ReconciliationPhase;
  readonly pass: number;
  readonly existingKeyCount: number;
  readonly existingBlockCount: number;
  readonly requiredKeyCount: number;
  readonly created: readonly string[];
  readonly deleted: readonly string[];
  readonly updated: readonly string[];
  readonly protectedKeys: readonly string[];
  readonly duplicateExistingKeys: readonly string[];
  readonly anomalyCodes: readonly ReconciliationAnomalyCode[];
  readonly timestampMs: number;
}

type MutationKind = 'create' | 'delete';

interface RecentMutation {
  readonly kind: MutationKind;
  readonly atMs: number;
}

const CHURN_WINDOW_MS = 30_000;
const MUTATION_BURST_THRESHOLD = 10;
const MAX_DETAIL_KEYS = 20;
const logger = new DebugLogger('affine:streamlain:reconciliation-invariants');
const emittedPassiveDiagnosticSignatures = new Set<string>();

function findDuplicates(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) duplicates.add(value);
    seen.add(value);
  }
  return Array.from(duplicates).sort();
}

/**
 * Detects reconciliation invariant violations independently of the concrete
 * Store implementation. The monitor is reusable by every persistent
 * desired-state reconciler that owns stable semantic keys.
 */
export class ReconciliationInvariantMonitor {
  private readonly recentMutations = new Map<string, RecentMutation>();

  constructor(private readonly now: () => number = Date.now) {}

  inspect(
    observation: ReconciliationObservation
  ): ReconciliationDiagnosticDetail {
    const anomalyCodes = new Set<ReconciliationAnomalyCode>();
    const requiredKeySet = new Set(observation.requiredKeys);
    const duplicateRequiredKeys = findDuplicates(observation.requiredKeys);
    const duplicateExistingKeys = Array.from(
      observation.existingByKey.entries()
    )
      .filter(([, blockIds]) => blockIds.length > 1)
      .map(([key]) => key)
      .sort();

    if (duplicateRequiredKeys.length > 0) {
      anomalyCodes.add('duplicate-required-key');
    }
    if (duplicateExistingKeys.length > 0) {
      anomalyCodes.add('duplicate-existing-key');
    }
    if (
      observation.phase !== 'readonly' &&
      Array.from(requiredKeySet).some(
        key => !observation.existingByKey.has(key)
      )
    ) {
      anomalyCodes.add('missing-required-key');
    }

    if (
      observation.phase === 'observing' &&
      (observation.created.length > 0 ||
        observation.deleted.length > 0 ||
        observation.updated.length > 0)
    ) {
      anomalyCodes.add('mutation-without-user-intent');
    }

    const mutationCount =
      observation.created.length +
      observation.deleted.length +
      observation.updated.length;
    if (mutationCount >= MUTATION_BURST_THRESHOLD) {
      anomalyCodes.add('mutation-burst');
    }

    const timestampMs = this.now();
    this.recordMutations(
      observation.created,
      'create',
      timestampMs,
      anomalyCodes
    );
    this.recordMutations(
      observation.deleted,
      'delete',
      timestampMs,
      anomalyCodes
    );
    this.pruneRecentMutations(timestampMs);

    return {
      reconciler: observation.reconciler,
      docId: observation.docId,
      phase: observation.phase,
      pass: observation.pass,
      existingKeyCount: observation.existingByKey.size,
      existingBlockCount: Array.from(observation.existingByKey.values()).reduce(
        (count, blockIds) => count + blockIds.length,
        0
      ),
      requiredKeyCount: requiredKeySet.size,
      created: observation.created.slice(0, MAX_DETAIL_KEYS),
      deleted: observation.deleted.slice(0, MAX_DETAIL_KEYS),
      updated: observation.updated.slice(0, MAX_DETAIL_KEYS),
      protectedKeys: observation.protectedKeys.slice(0, MAX_DETAIL_KEYS),
      duplicateExistingKeys: duplicateExistingKeys.slice(0, MAX_DETAIL_KEYS),
      anomalyCodes: Array.from(anomalyCodes).sort(),
      timestampMs,
    };
  }

  private recordMutations(
    keys: readonly string[],
    kind: MutationKind,
    timestampMs: number,
    anomalyCodes: Set<ReconciliationAnomalyCode>
  ): void {
    for (const key of keys) {
      const previous = this.recentMutations.get(key);
      if (
        previous &&
        previous.kind !== kind &&
        timestampMs - previous.atMs <= CHURN_WINDOW_MS
      ) {
        anomalyCodes.add(
          previous.kind === 'delete'
            ? 'delete-create-churn'
            : 'create-delete-churn'
        );
      }
      this.recentMutations.set(key, { kind, atMs: timestampMs });
    }
  }

  private pruneRecentMutations(timestampMs: number): void {
    for (const [key, mutation] of this.recentMutations) {
      if (timestampMs - mutation.atMs > CHURN_WINDOW_MS) {
        this.recentMutations.delete(key);
      }
    }
  }
}

export function emitReconciliationDiagnostic(
  detail: ReconciliationDiagnosticDetail
): void {
  const mutationCount =
    detail.created.length + detail.deleted.length + detail.updated.length;
  if (
    mutationCount === 0 &&
    detail.protectedKeys.length === 0 &&
    detail.anomalyCodes.length === 0
  ) {
    return;
  }

  if (mutationCount === 0) {
    const signature = [
      detail.docId,
      detail.reconciler,
      detail.phase,
      detail.anomalyCodes.join(','),
      detail.protectedKeys.join(','),
      detail.duplicateExistingKeys.join(','),
    ].join(':');
    if (emittedPassiveDiagnosticSignatures.has(signature)) return;
    emittedPassiveDiagnosticSignatures.add(signature);
  }

  logger.debug('Reconciliation diagnostic', detail);
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(CANVAS_RECONCILIATION_DIAGNOSTIC_EVENT, { detail })
  );
}
